import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";

import { EventsState } from "../../../../../../../../store/types";
import { Session, SessionTypesFeatures, EventSessionTabs, SessionTabs, SessionTypesEnum } from "../../../../../../../../types/working-model";
import usePrevious from "../../../../../../../../utils/use-previous";
import CollapsingList from "../../../../../../../general-ui/collapsing-list/collapsing-list";
import Icon, { ICONS } from "../../../../../../../general-ui/icon";
import Search from "../../../../../../../general-ui/search-bar/search";
import SelectCategories, { ALL_CATEGORIES } from "../../../../editor/agenda-editor/select-categories";
import SelectDates from "../../../../editor/agenda-editor/select-dates";
import SelectLanguages, { LanguageOption } from "../../../../editor/agenda-editor/select-languages";
import SessionList from "../../../sessions/session-list";
import TabHeader from "../../../../../../../general-ui/tab-header/tab-header";
import { capitalize } from "../../../../../../../../utils/utils";
import { PATHNAMES } from "../../../../../../admin";
import { NavMainItems } from '../../../../../../../../types/working-model';
import ActiveEventHeader from '../../headers/active-event-header';
import './sessions-panel.scss';

export const EMPTY_FILTER_STATE = {
	date: null,
	language: null,
	category: null,
	searchText: '',
};

export interface ISessionFilters {
	date: null | string;
	language: null | LanguageOption;
	category: null | string;
	searchText: string;
}
interface ISearchSessionsProps {
	sessions: Session[];
	setFilters: React.Dispatch<React.SetStateAction<ISessionFilters>>;
	filters: ISessionFilters;
	resetCounter: number;
	activeTab: string;
}

const SearchSessions: React.FC<ISearchSessionsProps> = ({
	sessions,
	setFilters,
	filters,
	resetCounter,
	activeTab
}) => {

	const [showFilter, setShowFilter] = useState(false);

	const handleFilterToggle = () => setShowFilter(prev => !prev);

	let sessionType = SessionTypesEnum.broadcast;
	if (activeTab === EventSessionTabs.OnDemand) {
		sessionType = SessionTypesEnum.onDemand;
	}

	const renderFilters = () => {
		switch (activeTab) {
			case SessionTabs.EventAgenda:
				return (
					<ul className="sessions-agenda-filter-list">
						<li>
							<SelectDates
								sessions={sessions}
								onSelect={session => {
									if (session?.timestamp) {
										const date = new Date(session.timestamp).toDateString();
										setFilters(prev => ({ ...prev, date }));
									} else {
										setFilters(prev => ({ ...prev, date: null }));
									}
								}}
								sessionType={sessionType}
								resetCounter={resetCounter}
							/>
						</li>
						<li>
							<SelectCategories
								sessions={sessions}
								onSelect={track => {
									if (track === ALL_CATEGORIES) {
										setFilters(prev => ({ ...prev, category: null }));
									} else {
										setFilters(prev => ({ ...prev, category: track }));
									}
								}}
								sessionType={sessionType}
								resetCounter={resetCounter}
							/>
						</li>
						<li>
							<SelectLanguages
								sessions={sessions}
								onSelect={language => {
									if (language.code === 'all') {
										setFilters(prev => ({ ...prev, language: null }));
									} else {
										setFilters(prev => ({ ...prev, language }));
									}
								}}
								sessionType={sessionType}
								resetCounter={resetCounter}
							/>
						</li>
					</ul>
				);
			case SessionTabs.OnDemand:
				return (
					<div className="sessions-on-demand-filter-list">
						<SelectLanguages
							sessions={sessions}
							onSelect={language => {
								if (language.code === 'all') {
									setFilters(prev => ({ ...prev, language: null }));
								} else {
									setFilters(prev => ({ ...prev, language }));
								}
							}}
							sessionType={sessionType}
							resetCounter={resetCounter}
						/>
					</div>
				);
			default:
				return null;
		}
	};

	return (
		<div className="sessions-list-filter-collapsing-list">
			<CollapsingList
				label=""
				customHeader={(toggleOpen) => (
					<div className="sessions-list-search-header">
						<div className="sessions-list-text-search-container">
							<Search
								isControlled
								controlledValue={filters.searchText}
								placeholder="Search session"
								onSearch={() => ({})}
								onDebounce={val => {
									setFilters(prev => ({ ...prev, searchText: val }));
								}}
								onClear={() => {
									setFilters(prev => ({ ...prev, searchText: '' }));
								}}
							/>
						</div>
						<button className="no-style clear" onClick={() => {
							handleFilterToggle();
							toggleOpen();
						}}>
							<Icon name={showFilter ? ICONS.FILTER_ACTIVE : ICONS.FILTER_INACTIVE} color='' size={32} />
						</button>
					</div>
				)}
			>
				{renderFilters()}
			</CollapsingList>
		</div>
	);
};

const SessionsList: React.FC = () => {
	const {
		CreateEventReducer: {
			workingEvent
		},
	} = useSelector((state: EventsState) => state);

	const [filters, setFilters] = useState<ISessionFilters>(EMPTY_FILTER_STATE);
	// incrementing resetCounter this will reset all filters
	const [resetCounter, setResetCounter] = useState(0);

	// get all sessions and split them between has start time and on demand
	const [timedSessions, onDemandSessions] = useMemo(() => {
		if (workingEvent?.sessions) {
			let timedSessionsArray: Session[] = [];
			let onDemandSessionsArray: Session[] = [];
			for (const session of workingEvent.sessions) {
				if (SessionTypesFeatures[session.session_type].has_start_time) {
					timedSessionsArray = [...timedSessionsArray, session];
				} else {
					onDemandSessionsArray = [...onDemandSessionsArray, session];
				}
			}
			return [timedSessionsArray, onDemandSessionsArray];
		} else {
			return [[], []];
		}
	}, [workingEvent?.sessions]);

	const sessionTypesTabs = useMemo(() => {
		const tabsList = [];
		timedSessions.length && tabsList.push(EventSessionTabs.EventAgenda);
		onDemandSessions.length && tabsList.push(EventSessionTabs.OnDemand);
		return tabsList;
	}, [timedSessions, onDemandSessions]);

	const { id, tab }: { id: string, tab?: string; } = useParams();
	const [activeTab, setActiveTab] = useState(tab ? capitalize(tab).replaceAll("-", " ") : sessionTypesTabs[0]);

	const history = useHistory();

	useEffect(() => {
		history.replace(PATHNAMES.Event[NavMainItems.Sessions](id, activeTab.toLowerCase().replaceAll(" ", "-")));
	}, [history, id, activeTab]);

	const previousActiveTab = usePrevious(activeTab);

	// if active tab changes, then reset the filters to null
	useEffect(() => {
		if (activeTab !== previousActiveTab) {
			setFilters(EMPTY_FILTER_STATE);
		}
	}, [activeTab, previousActiveTab]);

	const handleResetFilters = () => {
		setFilters(EMPTY_FILTER_STATE);
		const updatedReset = resetCounter + 1;
		setResetCounter(updatedReset);
	};

	const renderPanel = () => {
		switch (activeTab) {
			case EventSessionTabs.EventAgenda:
				return (
					<SessionList
						sessions={timedSessions}
						filters={filters}
						handleResetFilters={handleResetFilters}
					/>
				);
			case EventSessionTabs.OnDemand:
				return (
					<SessionList
						sessions={onDemandSessions}
						filters={filters}
						handleResetFilters={handleResetFilters}
					/>
				);
			default:
				return null;
		}
	};

	return (<>
		<ActiveEventHeader title={NavMainItems.Sessions} addSession>
			<TabHeader
				tabs={sessionTypesTabs}
				currentTab={activeTab}
				onClick={(tab: string) => setActiveTab(tab)}
			/>
		</ActiveEventHeader>
		<div className="panel-body">
			<div className="event-panel-body-sessions">
				<SearchSessions
					sessions={workingEvent?.sessions || []}
					setFilters={setFilters}
					filters={filters}
					resetCounter={resetCounter}
					activeTab={activeTab}
				/>
				{renderPanel()}
			</div>
		</div >
	</>);
};

export default SessionsList;
